import React, {  } from 'react';
import { FormHelperText, IconButton, Toolbar, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ImagePicker from '@dyl/components/ImagePicker';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import { useImage } from '@dyl/systems/ImagesManager';
import DragCanvas, { DragCanvasImage } from '@dyl/components/DragCanvas';
import { Assignment, FileCopy } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import PreviousImages from './utils/PreviousImages';

const useStyles = makeStyles(theme => ({
    canvas: {
        marginBottom: theme.spacing(2),
    },
    imagePicker: {
        marginBottom: theme.spacing(1),
    },
}));

export default function HighlightEditor({ index }) {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();

    const [backgroundImageId] = useTaskField('background_image');
    const { src: backgroundImageSrc } = useImage(backgroundImageId);

    const [promptImageId, setPromptImageId, promptImageError] = useTaskField('items', index, 'answers', 0, 'image_id');
    const { src: promptImageSrc } = useImage(promptImageId);

    const [rawCoordinates, setRawCoordinates, coordinatesError] = useTaskField('items', index, 'answers', 0, 'coordinates');

    const onCopyCoordinates = () => {
        navigator.clipboard.writeText(`${rawCoordinates.left},${rawCoordinates.top}`);
    };

    const onPasteCoordinates = async () => {
        const serialized = await navigator.clipboard.readText()
            .catch(error => {
                enqueueSnackbar('Failed to read clipboard.');

                throw error;
            });

        if (!serialized) {
            return enqueueSnackbar('Clipboard is empty.');
        }

        const match = /([0-9]+)\s*,\s*([0-9]+)/.exec(serialized);

        if (!match) {
            return enqueueSnackbar('Clipboard does not contain valid coordinates.');
        }

        const [, leftSerialized, topSerialized] = match;

        setRawCoordinates({
            top: Number.parseInt(topSerialized, 10),
            left: Number.parseInt(leftSerialized, 10),
        });
    };

    return <>
        <ImagePicker
            className={classes.imagePicker}
            value={promptImageId}
            onChange={id => setPromptImageId(id)}
            label='Prompt Image'
            helperText={promptImageError || ' '}
            error={!!promptImageError}
        />

        <Toolbar variant='dense' style={{ paddingLeft: 0 }}>
            <Tooltip title='Copy Coordinates'>
                <IconButton onClick={onCopyCoordinates} size="large">
                    <FileCopy />
                </IconButton>
            </Tooltip>

            <Tooltip title='Paste Coordinates'>
                <IconButton onClick={onPasteCoordinates} size="large">
                    <Assignment />
                </IconButton>
            </Tooltip>
        </Toolbar>

        <DragCanvas
            className={classes.canvas}
            backgroundSrc={backgroundImageSrc}
        >
            <PreviousImages index={index} />
            <DragCanvasImage
                src={promptImageSrc}
                alt={promptImageId}
                coordinates={rawCoordinates}
                onCoordinatesChange={setRawCoordinates}
            />
        </DragCanvas>

        {/* Error message for coordinates, if there is one */}
        <FormHelperText error={!!coordinatesError}>
            {coordinatesError || ' '}
        </FormHelperText>
    </>;
}